import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { OrganisationSettingsService } from '@/api';
import { collectPages } from '@/lib/collectPages';
import type { 
  Childcare, 
  CreateChildcareRequest, 
  UpdateChildcareRequest,
  PaginatedResponse 
} from '@/types/organisation';
import { toast } from 'sonner';
import { useLingui } from '@lingui/react/macro';

interface UseChildcaresOptions {
  limit?: number;
  offset?: number;
  /**
   * Walk every page and return the complete list. For callers that must not
   * miss a childcare — a filter built on a partial list silently hides rows —
   * rather than render one page of it.
   */
  all?: boolean;
  /** Set false to skip the request, e.g. until a caller knows it needs the list. */
  enabled?: boolean;
}

/** Page size for `all`; the backend clamps `limit` at 1000. */
const ALL_PAGE_LIMIT = 500;

/**
 * The generated `organisations_PaginatedResponse` types `data` as `any` (swag emits no item
 * type for the childcares list endpoint), which made every `childcares.map(cc => ...)` callback
 * across the app an implicit `any` (#1153). Narrow it once here so consumers get real types.
 */
type ChildcaresResponse = Omit<PaginatedResponse, 'data'> & { data?: Childcare[] };

export function useChildcares(options: UseChildcaresOptions = {}) {
  const { orgId, isSignedIn } = useAuth();

  const { limit = 10, offset = 0, all = false, enabled = true } = options;

  return useQuery({
    // `all` keeps its own cache entry: it answers a different question from any
    // single page, and sharing a key with one would serve a page as the whole
    // list on first paint.
    queryKey: ['childcares', orgId, all ? { all: true } : { limit, offset }],
    queryFn: async () => {
      if (!orgId) throw new Error('No organisation ID available');
      if (all) {
        const items = await collectPages<Childcare>(async (pageOffset) => {
          const page = await OrganisationSettingsService.getApiV1OrganisationsChildcares(
            orgId,
            ALL_PAGE_LIMIT,
            pageOffset
          );
          return {
            items: (page.data ?? []) as Childcare[],
            total: page.totalCount,
          };
        });
        // No limit/offset: this is the complete list, not a window into one.
        const complete: ChildcaresResponse = {
          data: items,
          totalCount: items.length,
        };
        return complete;
      }
      const response = await OrganisationSettingsService.getApiV1OrganisationsChildcares(
        orgId,
        limit,
        offset
      );
      return response as ChildcaresResponse;
    },
    enabled: enabled && !!orgId && isSignedIn,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useChildcare(childcareId: string) {
  const { orgId } = useAuth();

  return useQuery({
    queryKey: ['childcare', childcareId],
    queryFn: async () => {
      if (!childcareId) throw new Error('No childcare ID provided');
      // Since there's no direct get endpoint, we'll need to fetch from the list
      // This is a workaround - ideally the backend would provide a GET endpoint
      const response = await OrganisationSettingsService.getApiV1OrganisationsChildcares(
        orgId!,
        100,
        0
      );
      const childcare = (response as PaginatedResponse).data?.find(
        (c: Childcare) => c.id === childcareId
      );
      if (!childcare) throw new Error('Childcare not found');
      return childcare as Childcare;
    },
    enabled: !!orgId && !!childcareId,
  });
}

export function useCreateChildcare() {
  const { orgId } = useAuth();
  const queryClient = useQueryClient();
  const { t } = useLingui();

  return useMutation({
    mutationFn: async (data: CreateChildcareRequest) => {
      if (!orgId) throw new Error('No organisation ID available');
      
      const response = await OrganisationSettingsService.postApiV1OrganisationsChildcares(
        orgId,
        data
      );
      return response as Childcare;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['childcares', orgId] });
      toast.success(t`Childcare location created successfully`);
    },
    onError: () => {
      toast.error(t`Failed to create childcare location`);
    },
  });
}

export function useUpdateChildcare() {
  const { orgId } = useAuth();
  const queryClient = useQueryClient();
  const { t } = useLingui();

  return useMutation({
    mutationFn: async ({ 
      id, 
      data 
    }: { 
      id: string; 
      data: UpdateChildcareRequest 
    }) => {
      const response = await OrganisationSettingsService.putApiV1Childcares(id, data);
      return response as Childcare;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['childcares', orgId] });
      queryClient.invalidateQueries({ queryKey: ['childcare', variables.id] });
      toast.success(t`Childcare location updated successfully`);
    },
    onError: () => {
      toast.error(t`Failed to update childcare location`);
    },
  });
}

export function useDeleteChildcare() {
  const { orgId } = useAuth();
  const queryClient = useQueryClient();
  const { t } = useLingui();

  return useMutation({
    mutationFn: async (childcareId: string) => {
      await OrganisationSettingsService.deleteApiV1Childcares(childcareId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['childcares', orgId] });
      toast.success(t`Childcare location deleted successfully`);
    },
    onError: () => {
      toast.error(t`Failed to delete childcare location`);
    },
  });
}